/* eslint-disable camelcase */

import { useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { kueskiAttribution } from '@kueski-dev/web-observability';

import { payGTMSessionState } from 'Services/GTM/GTMSessionState';
import { useSessionReady } from 'Services/SessionReady';
import { Auth0User } from './useUserActivity.types';
import {
  extractAttributionParams,
  getGTMAttributionFromURL,
  urlHasTrackingParams,
} from './useUserActivity.utils';

function softUpdate(user: string) {
  const readData = extractAttributionParams(kueskiAttribution.data);
  payGTMSessionState.safeUpdate(user, readData);
}

export const useUserActivity = () => {
  const { sessionReady } = useSessionReady();
  const { user } = useAuth0<Auth0User>();
  const { pathname } = useLocation();
  const gtmAttribution = useMemo(() => getGTMAttributionFromURL(window.location.href), []);

  useEffect(() => {
    if (!user?.kueski_user_id || !sessionReady) return;
    if (urlHasTrackingParams(gtmAttribution)) {
      payGTMSessionState.forceUpdate(user.kueski_user_id, gtmAttribution);
    } else {
      softUpdate(user.kueski_user_id);
    }
  }, [user?.kueski_user_id, sessionReady]);

  useEffect(() => {
    if (!user?.kueski_user_id || !sessionReady) return;
    softUpdate(user.kueski_user_id);
  }, [user?.kueski_user_id, pathname, sessionReady]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        softUpdate(user.kueski_user_id);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.kueski_user_id]);

  useEffect(() => {
    if (urlHasTrackingParams(gtmAttribution)) {
      kueskiAttribution.update(gtmAttribution);
    }
  }, []);
};
